import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { adminApi, apiError } from "@/components/admin/api";
import { formatPrice } from "@/data/site";

const EMPTY_FORM = {
  title: "",
  description: "",
  category: "Psicoterapia",
  modalities: [],
  duration: "50 minutos",
  frequency: "",
  plans: [{ name: "Sessão avulsa", sessions: 1, price: null }],
  show_price: false,
  active: true,
  order: 10,
};

const inputCls =
  "w-full rounded-2xl border border-raiz-800/20 bg-paper px-4 py-3 text-sm text-ink placeholder:text-ink/35 focus:border-raiz-700 focus:outline-none";

const ServiceForm = ({ initial, saving, onSave, onCancel, testIdPrefix }) => {
  const [form, setForm] = useState(initial);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPlan = (i, k, v) =>
    setForm((f) => ({ ...f, plans: f.plans.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));

  const toggleModality = (m) =>
    set("modalities", form.modalities.includes(m) ? form.modalities.filter((x) => x !== m) : [...form.modalities, m]);

  const submit = () => {
    if (form.title.trim().length < 2) return toast.error("Informe o nome do serviço.");
    if (form.modalities.length === 0) return toast.error("Escolha ao menos uma modalidade.");
    if (form.plans.length === 0) return toast.error("Adicione ao menos um formato (avulso, pacote…).");
    if (form.plans.some((p) => p.name.trim().length < 2)) return toast.error("Todo formato precisa de um nome.");
    onSave({
      ...form,
      plans: form.plans.map((p) => ({
        ...(p.id ? { id: p.id } : {}),
        name: p.name.trim(),
        sessions: p.sessions ? Number(p.sessions) : null,
        price: p.price === "" || p.price === null || p.price === undefined ? null : Number(p.price),
      })),
    });
  };

  return (
    <div className="rounded-3xl border border-gold-400/50 bg-gold-100/30 p-6 sm:p-8" data-testid={`${testIdPrefix}-form`}>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-2 block text-sm font-medium text-ink">Nome do serviço</label>
          <input className={inputCls} data-testid={`${testIdPrefix}-nome`} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex.: Psicoterapia Online" />
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-ink">Categoria</label>
          <input className={inputCls} data-testid={`${testIdPrefix}-categoria`} value={form.category} onChange={(e) => set("category", e.target.value)} />
        </div>
      </div>
      <div className="mt-5">
        <label className="mb-2 block text-sm font-medium text-ink">Descrição</label>
        <textarea rows={2} className={`${inputCls} resize-none`} data-testid={`${testIdPrefix}-descricao`} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Resumo administrativo do serviço" />
      </div>
      <div className="mt-5 grid gap-5 sm:grid-cols-3">
        <div>
          <label className="mb-2 block text-sm font-medium text-ink">Modalidades</label>
          <div className="flex gap-2">
            {["online", "presencial"].map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`${testIdPrefix}-modalidade-${m}`}
                onClick={() => toggleModality(m)}
                className={`rounded-full border px-4 py-2 text-xs font-medium capitalize transition-colors ${
                  form.modalities.includes(m) ? "border-raiz-800 bg-raiz-800 text-paper" : "border-raiz-800/25 text-raiz-900 hover:border-raiz-800"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-ink">Duração</label>
          <input className={inputCls} data-testid={`${testIdPrefix}-duracao`} value={form.duration} onChange={(e) => set("duration", e.target.value)} />
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-ink">Frequência</label>
          <input className={inputCls} data-testid={`${testIdPrefix}-frequencia`} value={form.frequency} onChange={(e) => set("frequency", e.target.value)} placeholder="Ex.: Semanal" />
        </div>
      </div>

      <div className="mt-6">
        <p className="mb-3 text-sm font-medium text-ink">Formatos e preços</p>
        <div className="space-y-3">
          {form.plans.map((p, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 rounded-2xl border border-raiz-900/10 bg-paper p-3" data-testid={`${testIdPrefix}-plano-${i}`}>
              <input className={`${inputCls} flex-1 min-w-36`} placeholder="Nome (ex.: Pacote de 4 sessões)" value={p.name} onChange={(e) => setPlan(i, "name", e.target.value)} data-testid={`${testIdPrefix}-plano-nome-${i}`} />
              <input className={`${inputCls} w-28`} type="number" min={1} placeholder="Sessões" value={p.sessions ?? ""} onChange={(e) => setPlan(i, "sessions", e.target.value)} data-testid={`${testIdPrefix}-plano-sessoes-${i}`} />
              <input className={`${inputCls} w-32`} type="number" min={0} step="0.01" placeholder="Preço R$" value={p.price ?? ""} onChange={(e) => setPlan(i, "price", e.target.value)} data-testid={`${testIdPrefix}-plano-preco-${i}`} />
              <button type="button" aria-label="Remover formato" data-testid={`${testIdPrefix}-plano-remover-${i}`} onClick={() => set("plans", form.plans.filter((_, j) => j !== i))} className="text-ink/40 hover:text-red-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          data-testid={`${testIdPrefix}-plano-adicionar`}
          onClick={() => set("plans", [...form.plans, { name: "", sessions: 1, price: null }])}
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-raiz-800/25 px-4 py-2 text-xs font-medium text-raiz-800 hover:border-raiz-800"
        >
          <Plus className="h-3.5 w-3.5" />
          Adicionar formato
        </button>
        <p className="mt-2 text-xs text-ink/45">Preço vazio = "valor sob consulta". Sessões vazia = processo sem número fixo.</p>
      </div>

      <div className="mt-6 flex flex-wrap gap-6">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink/75">
          <input type="checkbox" data-testid={`${testIdPrefix}-ativo`} checked={form.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-raiz-800" />
          Serviço ativo (visível no site)
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink/75">
          <input type="checkbox" data-testid={`${testIdPrefix}-show-price`} checked={form.show_price} onChange={(e) => set("show_price", e.target.checked)} className="h-4 w-4 accent-raiz-800" />
          Exibir preço no site
        </label>
      </div>

      <div className="mt-7 flex gap-3">
        <button type="button" data-testid={`${testIdPrefix}-salvar`} onClick={submit} disabled={saving} className="rounded-full bg-raiz-800 px-6 py-3 text-sm font-medium text-paper hover:bg-raiz-700 disabled:opacity-60">
          {saving ? "Salvando…" : "Salvar serviço"}
        </button>
        <button type="button" data-testid={`${testIdPrefix}-cancelar`} onClick={onCancel} className="rounded-full border border-raiz-800/25 px-6 py-3 text-sm text-raiz-800 hover:border-raiz-800">
          Cancelar
        </button>
      </div>
    </div>
  );
};

const ServicesView = () => {
  const [services, setServices] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    adminApi
      .get("/admin/journey/services")
      .then((res) => setServices(res.data))
      .catch((e) => toast.error(apiError(e, "Não foi possível carregar os serviços.")));
  }, []);

  useEffect(load, [load]);

  const save = async (slug, payload) => {
    setSaving(true);
    try {
      if (slug === "novo") {
        await adminApi.post("/admin/journey/services", payload);
        toast.success("Serviço criado.");
      } else {
        await adminApi.put(`/admin/journey/services/${slug}`, payload);
        toast.success("Serviço atualizado.");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await adminApi.put(`/admin/journey/services/${s.slug}`, { ...s, active: !s.active });
      toast.success(s.active ? "Serviço desativado — não aparece mais no site." : "Serviço ativado — visível no site.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const togglePrice = async (s) => {
    try {
      await adminApi.put(`/admin/journey/services/${s.slug}`, { ...s, show_price: !s.show_price });
      toast.success(s.show_price ? "Preço oculto no site." : "Preço visível no site.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Excluir o serviço "${s.title}"?`)) return;
    try {
      await adminApi.delete(`/admin/journey/services/${s.slug}`);
      toast.success("Serviço excluído.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!services) {
    return <div className="h-64 animate-pulse rounded-3xl border border-raiz-900/10 bg-cream" data-testid="servicos-loading" />;
  }

  return (
    <div data-testid="servicos-view">
      <div className="flex items-center justify-between">
        <p className="max-w-xl text-sm leading-relaxed text-ink/60">
          Gerencie os atendimentos do site. Serviços inativos não aparecem para os pacientes.
        </p>
        <button
          type="button"
          data-testid="servico-novo-button"
          onClick={() => setEditing("novo")}
          className="inline-flex items-center gap-2 rounded-full bg-raiz-800 px-5 py-2.5 text-xs font-medium text-paper hover:bg-raiz-700"
        >
          <Plus className="h-4 w-4" />
          Novo serviço
        </button>
      </div>

      {editing === "novo" && (
        <div className="mt-6">
          <ServiceForm initial={EMPTY_FORM} saving={saving} onSave={(p) => save("novo", p)} onCancel={() => setEditing(null)} testIdPrefix="servico-novo" />
        </div>
      )}

      <div className="mt-6 space-y-4" data-testid="servicos-lista">
        {services.map((s) => (
          <div key={s.slug} data-testid={`servico-card-${s.slug}`} className="rounded-3xl border border-raiz-900/10 bg-paper p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-serif text-xl text-ink">{s.title}</h3>
                  <span className={`rounded-full border px-3 py-1 text-xs font-medium ${s.active ? "border-emerald-600/40 text-emerald-800" : "border-neutral-400/50 text-neutral-500"}`} data-testid={`servico-status-${s.slug}`}>
                    {s.active ? "Ativo" : "Inativo"}
                  </span>
                  {s.show_price && (
                    <span className="rounded-full border border-gold-500/50 px-3 py-1 text-xs font-medium text-gold-700">Preço visível no site</span>
                  )}
                </div>
                {s.description && <p className="mt-2 max-w-xl text-sm text-ink/60">{s.description}</p>}
                <p className="mt-2 text-xs text-ink/45">
                  {s.modalities.map((m) => m === "online" ? "Online" : "Presencial").join(" • ")} — {s.duration}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {s.plans.map((p) => (
                    <span key={p.id} className="rounded-full border border-raiz-800/15 bg-cream px-3.5 py-1.5 text-xs text-raiz-900" data-testid={`servico-plano-${s.slug}-${p.id}`}>
                      {p.name}
                      {p.sessions ? ` (${p.sessions})` : ""} — {p.price != null ? formatPrice(p.price) : "sob consulta"}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" data-testid={`servico-editar-${s.slug}`} onClick={() => setEditing(editing === s.slug ? null : s.slug)} className="inline-flex items-center gap-2 rounded-full border border-raiz-800/25 px-4 py-2 text-xs font-medium text-raiz-800 hover:border-raiz-800">
                  <Pencil className="h-3.5 w-3.5" />
                  Editar
                </button>
                <button type="button" data-testid={`servico-toggle-${s.slug}`} onClick={() => toggleActive(s)} className="rounded-full border border-raiz-800/25 px-4 py-2 text-xs font-medium text-raiz-800 hover:border-raiz-800">
                  {s.active ? "Desativar" : "Ativar"}
                </button>
                <button type="button" data-testid={`servico-preco-toggle-${s.slug}`} onClick={() => togglePrice(s)} className="rounded-full border border-gold-500/50 px-4 py-2 text-xs font-medium text-gold-700 hover:bg-gold-100/50">
                  {s.show_price ? "Ocultar preço" : "Exibir preço"}
                </button>
                <button type="button" data-testid={`servico-excluir-${s.slug}`} onClick={() => remove(s)} className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-4 py-2 text-xs font-medium text-red-700 hover:bg-red-50">
                  <Trash2 className="h-3.5 w-3.5" />
                  Excluir
                </button>
              </div>
            </div>
            {editing === s.slug && (
              <div className="mt-6">
                <ServiceForm initial={s} saving={saving} onSave={(p) => save(s.slug, p)} onCancel={() => setEditing(null)} testIdPrefix={`servico-edit-${s.slug}`} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicesView;
